import React, { useEffect, useMemo, useState } from 'react'
import api from '../../services/api'
import Icon from '../../components/Icon'
import { Button, Input, Select } from '../../components/ui'
import { translateApiError } from '../../utils/errorMessages'

const getMemberId = (member) => String(member?.id ?? member?.employee_id ?? '')
const getMemberName = (member) => member?.full_name || member?.employee_name || ''
const getMemberBasePercentage = (member) => member?.base_percentage ?? member?.percentage ?? 0
const getMemberIsTeamLead = (member) => member?.is_team_lead === true || member?.is_team_lead === 1 || String(member?.is_team_lead || '').toLowerCase() === 'true'
const getMemberSubTeam = (member) => String(member?.sub_team || '').trim()

function TeamCard({ team, employees = [], canEdit = false, onChanged, onEditTeam, onDeleteTeam }) {
  const [members, setMembers] = useState(team?.members || [])
  const [draftPercentages, setDraftPercentages] = useState({})
  const [draftTeamLeads, setDraftTeamLeads] = useState({})
  const [draftSubTeams, setDraftSubTeams] = useState({})
  const [selectedEmployeeId, setSelectedEmployeeId] = useState('')
  const [newMemberPct, setNewMemberPct] = useState('0')
  const [newMemberSubTeam, setNewMemberSubTeam] = useState('')
  const [saving, setSaving] = useState(false)
  const [message, setMessage] = useState('')

  useEffect(() => {
    const nextMembers = team?.members || []
    setMembers(nextMembers)
    setDraftPercentages((prev) => {
      const nextDraft = {}
      nextMembers.forEach((m) => {
        const memberId = getMemberId(m)
        if (!memberId) return
        const currentValue = prev[memberId]
        nextDraft[memberId] = currentValue ?? String(getMemberBasePercentage(m))
      })
      return nextDraft
    })
    setDraftTeamLeads((prev) => {
      const nextDraft = {}
      nextMembers.forEach((m) => {
        const memberId = getMemberId(m)
        if (!memberId) return
        const currentValue = prev[memberId]
        nextDraft[memberId] = currentValue ?? getMemberIsTeamLead(m)
      })
      return nextDraft
    })
    setDraftSubTeams((prev) => {
      const nextDraft = {}
      nextMembers.forEach((m) => {
        const memberId = getMemberId(m)
        if (!memberId) return
        const currentValue = prev[memberId]
        nextDraft[memberId] = currentValue ?? getMemberSubTeam(m)
      })
      return nextDraft
    })
    setMessage('')
  }, [team])

  const availableEmployees = useMemo(() => {
    const existingIds = new Set((members || []).map((m) => getMemberId(m)))
    return (employees || []).filter((e) => !existingIds.has(String(e.id)))
  }, [employees, members])

  const subTeamOptions = useMemo(() => {
    const options = new Set(team?.sub_teams || [])
    Object.values(draftSubTeams).forEach((value) => {
      const normalized = String(value || '').trim()
      if (normalized) options.add(normalized)
    })
    if (newMemberSubTeam.trim()) options.add(newMemberSubTeam.trim())
    return [...options].sort((a, b) => a.localeCompare(b, 'ru'))
  }, [draftSubTeams, newMemberSubTeam, team?.sub_teams])

  const totalPercentage = useMemo(() => {
    return Object.values(draftPercentages).reduce((sum, value) => {
      const n = Number(String(value).replace(',', '.'))
      return sum + (Number.isFinite(n) ? n : 0)
    }, 0)
  }, [draftPercentages])

  const hasOverflow = totalPercentage > 100.0001

  const subTeamValidationError = useMemo(() => {
    const values = (members || []).map((member) => String(draftSubTeams[getMemberId(member)] || '').trim())
    const hasAny = values.some(Boolean)
    if (!hasAny) return ''
    if (values.some((value) => !value)) {
      return 'Если у одного участника задана подкоманда, её нужно указать для всех участников команды.'
    }
    return ''
  }, [draftSubTeams, members])

  const setMemberDraft = (employeeId, nextValue) => {
    setDraftPercentages((prev) => ({
      ...prev,
      [employeeId]: nextValue,
    }))
    setMessage('')
  }

  const setMemberLeadDraft = (employeeId, nextValue) => {
    setDraftTeamLeads((prev) => ({
      ...prev,
      [employeeId]: nextValue,
    }))
    setMessage('')
  }

  const setMemberSubTeamDraft = (employeeId, nextValue) => {
    setDraftSubTeams((prev) => ({
      ...prev,
      [employeeId]: nextValue,
    }))
    setMessage('')
  }

  const handleSaveMembers = async () => {
    if (subTeamValidationError) {
      setMessage(subTeamValidationError)
      return
    }
    try {
      setSaving(true)
      setMessage('')
      const payloadMembers = (members || []).map((m) => {
        const memberId = getMemberId(m)
        const raw = draftPercentages[memberId]
        const pct = Number(String(raw ?? 0).replace(',', '.'))
        return {
          employee_id: memberId,
          base_percentage: Number.isFinite(pct) ? pct : Number(getMemberBasePercentage(m) || 0),
          is_team_lead: Boolean(draftTeamLeads[memberId]),
          sub_team: String(draftSubTeams[memberId] || '').trim() || null,
        }
      }).filter((m) => m.employee_id)
      await api.put(`/company/teams/${team.id}/members/batch`, {
        members: payloadMembers,
        replace: true,
      })
      if (onChanged) {
        await onChanged()
      }
      setMessage('Состав команды сохранен')
    } catch (error) {
      setMessage(`Ошибка сохранения: ${translateApiError(error.response?.data?.error || error.message)}`)
    } finally {
      setSaving(false)
    }
  }

  const handleAddMember = async () => {
    if (!selectedEmployeeId) return
    if (subTeamValidationError) {
      setMessage(subTeamValidationError)
      return
    }
    const subTeam = String(newMemberSubTeam || '').trim()
    const teamUsesSubTeams = Boolean(team?.uses_sub_teams || subTeamOptions.length)
    if (teamUsesSubTeams && !subTeam) {
      setMessage('Укажите подкоманду для нового участника.')
      return
    }
    try {
      setSaving(true)
      setMessage('')
      const pct = Number(String(newMemberPct || '0').replace(',', '.'))
      await api.post(`/company/teams/${team.id}/members`, {
        employee_id: selectedEmployeeId,
        base_percentage: Number.isFinite(pct) ? pct : 0,
        is_team_lead: false,
        sub_team: subTeam || null,
      })
      if (onChanged) {
        await onChanged()
      }
      setSelectedEmployeeId('')
      setNewMemberPct('0')
      setNewMemberSubTeam('')
    } catch (error) {
      setMessage(`Ошибка добавления: ${translateApiError(error.response?.data?.error || error.message)}`)
    } finally {
      setSaving(false)
    }
  }

  const handleRemoveMember = async (employeeId) => {
    const memberKey = String(employeeId || '')
    if (!memberKey) return
    try {
      setSaving(true)
      setMessage('')
      await api.delete(`/company/teams/${team.id}/members/${memberKey}`)
      if (onChanged) {
        await onChanged()
      }
    } catch (error) {
      setMessage(`Ошибка удаления: ${translateApiError(error.response?.data?.error || error.message)}`)
    } finally {
      setSaving(false)
    }
  }

  const renderSubTeamField = (memberId, value, ariaLabel) => {
    if (!canEdit) {
      return <span>{value || '—'}</span>
    }
    return (
      <Input
        type="text"
        list={`sub-team-options-${team.id}`}
        className="org-team-card__sub-team-field"
        value={value || ''}
        onChange={(e) => setMemberSubTeamDraft(memberId, e.target.value)}
        placeholder="Подкоманда"
        aria-label={ariaLabel}
      />
    )
  }

  return (
    <section className="org-team-card ui-card">
      <datalist id={`sub-team-options-${team.id}`}>
        {subTeamOptions.map((option) => (
          <option key={option} value={option} />
        ))}
      </datalist>

      <div className="org-team-card__header">
        <div>
          <h3>{team.name}</h3>
          <p className="org-team-card__meta">
            Участников: {members.length} · Сумма процентов: {totalPercentage.toFixed(2)}%
            {team?.uses_sub_teams ? ` · Подкоманд: ${team.sub_teams?.length || subTeamOptions.length}` : ''}
          </p>
        </div>
        {canEdit && (
          <div className="org-team-card__actions">
            <Button
              type="button"
              variant="ghost"
              onClick={() => onEditTeam && onEditTeam(team)}
              aria-label="Редактировать команду"
              title="Редактировать команду"
            >
              <Icon name="edit" size={16} />
            </Button>
          </div>
        )}
      </div>

      {hasOverflow && (
        <p className="org-team-card__warning" role="alert">
          <Icon name="warning" size={16} /> Сумма процентов превышает 100%. Сохранение заблокировано.
        </p>
      )}
      {subTeamValidationError && (
        <p className="org-team-card__warning" role="alert">
          <Icon name="warning" size={16} /> {subTeamValidationError}
        </p>
      )}
      {message && <p className="org-team-card__message">{message}</p>}

      <div className="org-team-card__members">
        {(members || []).length === 0 ? (
          <p className="info-note">В команде пока нет сотрудников</p>
        ) : (
          <table className="ui-table data-table">
            <thead>
              <tr>
                <th>Сотрудник</th>
                <th>Подкоманда</th>
                <th>Процент бюджета</th>
                <th>Руководитель команды</th>
                {canEdit && <th>Действие</th>}
              </tr>
            </thead>
            <tbody>
              {(members || []).map((member) => {
                const memberId = getMemberId(member)
                return (
                  <tr key={memberId || getMemberName(member)}>
                    <td>{getMemberName(member) || '-'}</td>
                    <td>
                      {renderSubTeamField(
                        memberId,
                        draftSubTeams[memberId] ?? getMemberSubTeam(member),
                        `Подкоманда сотрудника ${getMemberName(member) || memberId}`,
                      )}
                    </td>
                    <td>
                      {canEdit ? (
                        <Input
                          type="number"
                          min={0}
                          max={100}
                          step={0.01}
                          value={draftPercentages[memberId] ?? String(getMemberBasePercentage(member) || 0)}
                          onChange={(e) => setMemberDraft(memberId, e.target.value)}
                          aria-label={`Процент сотрудника ${getMemberName(member) || memberId}`}
                        />
                      ) : (
                        <span>{Number(getMemberBasePercentage(member) || 0).toFixed(2)}%</span>
                      )}
                    </td>
                    <td>
                      {canEdit ? (
                        <label className="org-team-card__lead-toggle">
                          <input
                            type="checkbox"
                            checked={Boolean(draftTeamLeads[memberId])}
                            onChange={(e) => setMemberLeadDraft(memberId, e.target.checked)}
                            aria-label={`Руководитель команды: ${getMemberName(member) || memberId}`}
                          />
                          <span>Руководитель</span>
                        </label>
                      ) : (
                        <span>{getMemberIsTeamLead(member) ? 'Да' : '—'}</span>
                      )}
                    </td>
                    {canEdit && (
                      <td>
                        <Button
                          type="button"
                          variant="danger"
                          onClick={() => handleRemoveMember(memberId)}
                          disabled={saving}
                        >
                          Удалить
                        </Button>
                      </td>
                    )}
                  </tr>
                )
              })}
            </tbody>
          </table>
        )}
      </div>

      {canEdit && (
        <div className="org-team-card__footer">
          <div className="org-team-card__add-member">
            <Select
              value={selectedEmployeeId}
              onChange={(e) => setSelectedEmployeeId(e.target.value)}
              aria-label="Выбрать сотрудника"
            >
              <option value="">Добавить сотрудника…</option>
              {availableEmployees.map((employee) => (
                <option key={employee.id} value={employee.id}>
                  {employee.full_name}
                </option>
              ))}
            </Select>
            <Input
              type="text"
              list={`sub-team-options-${team.id}`}
              value={newMemberSubTeam}
              onChange={(e) => setNewMemberSubTeam(e.target.value)}
              placeholder="Подкоманда"
              aria-label="Подкоманда нового сотрудника"
            />
            <Input
              type="number"
              min={0}
              max={100}
              step={0.01}
              value={newMemberPct}
              onChange={(e) => setNewMemberPct(e.target.value)}
              aria-label="Процент нового сотрудника"
            />
            <Button type="button" variant="secondary" onClick={handleAddMember} disabled={!selectedEmployeeId || saving}>
              Добавить
            </Button>
          </div>
          <Button type="button" variant="primary" onClick={handleSaveMembers} disabled={saving || hasOverflow || Boolean(subTeamValidationError)}>
            Сохранить
          </Button>
          <Button
            type="button"
            variant="secondary"
            className="org-team-card__delete-btn"
            onClick={() => onDeleteTeam && onDeleteTeam(team)}
            disabled={saving}
          >
            Удалить команду
          </Button>
        </div>
      )}
    </section>
  )
}

export default TeamCard
